import { Dispatch, SetStateAction, useMemo } from 'react';
import { UseQueryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DefaultApi,
  ModelAccountSwitchRequest,
  ModelAccountSwitchResponse,
  ModelGetLoggedInUsersMetadataResponse,
} from '@rbx/client-account-switcher/v1';
import { Configuration } from '@rbx/clients-core';
import { useRobloxAuthentication } from '@rbx/auth';
import { useTranslation } from '@rbx/intl';
import { useSessionStorage } from '@rbx/react-utilities';
import useNavigationConfigs from '../hooks/useNavigationConfigs';
import { getBEDEV2ServiceBasePathV2 } from '../utils/getBasePaths';
import getProductHref from '../utils/getProductHref';
import { readRBXASBlob, syncRBXASBlob } from './useAccountSwitcherFrame';
import { AccountSwitcherSnackbarConfig } from './components/AccountSwitcherSnackbar';
import {
  switchAccountsFailedEventModel,
  switchAccountsSuccessEventModel,
} from '../event/eventConstants';
import {
  SWITCHED_ACCOUNT_USERS_STORAGE_KEY,
  SwitchedAccountUsersStorage,
} from './constants/storage';

const KEY_PREFIX = 'accountSwitcher_';
const METADATA_STALE_TIME = 10 * 1000; // 10 seconds

function useAccountSwitcherApi() {
  const { target, robloxEnvironment: environment } = useNavigationConfigs();

  return useMemo(() => {
    const basePath = getBEDEV2ServiceBasePathV2('account-switcher', target, environment);

    const configuration = new Configuration({
      basePath,
      credentials: 'include',
    });

    return new DefaultApi(configuration);
  }, [target, environment]);
}

export function useGetLoggedInUsersMetadata(
  options?: Partial<UseQueryOptions<ModelGetLoggedInUsersMetadataResponse>>,
) {
  const accountSwitcherApi = useAccountSwitcherApi();
  const { user, isFetched: isUserFetched } = useRobloxAuthentication();
  const { target, environment } = useNavigationConfigs();

  return useQuery({
    enabled: options?.enabled && isUserFetched && user !== null,
    queryFn: async () => {
      const rbxAsBlob = await readRBXASBlob(target, environment);
      const response = await accountSwitcherApi.accountSwitchGetLoggedInUsersMetadata({
        post: { encryptedUsersDataBlob: rbxAsBlob, removeInvalidActiveUser: false },
      });
      if (response.encryptedUsersDataBlob && rbxAsBlob !== response.encryptedUsersDataBlob) {
        await syncRBXASBlob(target, environment, response.encryptedUsersDataBlob);
      }
      return response;
    },
    queryKey: [`${KEY_PREFIX}getLoggedInUsersMetadata`],
    retry: options?.retry,
    staleTime: options?.staleTime ?? METADATA_STALE_TIME,
  });
}

export function useSwitchAccounts(
  setIsDialogOpen: (isOpen: boolean) => void,
  setSnackbarConfig: Dispatch<SetStateAction<AccountSwitcherSnackbarConfig | undefined>>,
) {
  const queryClient = useQueryClient();
  const accountSwitcherApi = useAccountSwitcherApi();
  const { translate } = useTranslation();
  const { currentProduct, target, environment, sendEvent } = useNavigationConfigs();
  const [, setSwitchedAccountUsersStorage] = useSessionStorage<SwitchedAccountUsersStorage | null>(
    SWITCHED_ACCOUNT_USERS_STORAGE_KEY,
    null,
  );

  return useMutation({
    mutationFn: async (request: Omit<ModelAccountSwitchRequest, 'encryptedUsersDataBlob'>) => {
      const rbxAsBlob = await readRBXASBlob(target, environment);

      const response = await accountSwitcherApi.accountSwitch({
        post: { ...request, encryptedUsersDataBlob: rbxAsBlob },
      });

      if (!response.encryptedUsersDataBlob) {
        throw new Error('Switch accounts failed: encryptedUsersDataBlob was null');
      }
      if (response.errors && response.errors.length > 0) {
        throw new Error(
          `Switch accounts failed: ${response.errors.map((error) => `Error code ${error.code}: ${error.message}`).join(', ')}`,
        );
      }
      await syncRBXASBlob(target, environment, response.encryptedUsersDataBlob);
      return response as ModelAccountSwitchResponse & { encryptedUsersDataBlob: string };
    },
    onError: (error, variables) => {
      sendEvent(
        switchAccountsFailedEventModel({
          switchedFromUserId: variables.switchedFromUserId ?? '',
          switchedToUserId: variables.switchedToUserId ?? '',
          error: error.toString(),
        }),
      );
      setIsDialogOpen(false);
      setSnackbarConfig({
        title: translate('Message.AccountSwitchFailed'),
        isError: true,
      });
      queryClient.resetQueries({ queryKey: [`${KEY_PREFIX}getLoggedInUsersMetadata`] });
    },
    onSuccess: (response, variables) => {
      sendEvent(
        switchAccountsSuccessEventModel({
          switchedFromUserId: variables.switchedFromUserId ?? '',
          switchedToUserId: variables.switchedToUserId ?? '',
        }),
      );
      setSwitchedAccountUsersStorage({
        switchedFromUserId: Number(variables.switchedFromUserId),
        switchedToUserId: Number(variables.switchedToUserId),
      });
      setTimeout(() => {
        window.open(getProductHref(currentProduct, window.location.origin), '_self');
      }, 100);
    },
    mutationKey: [`${KEY_PREFIX}useSwitchAccounts`],
  });
}
